// src\themes\lino\elements\Header\HeaderNavSearch.tsx
"use client"

import { useState, useRef, useEffect } from "react"
import { Clock, Search, X } from "lucide-react"
import { useRouter } from "next/navigation"
import { useDictionary } from "@/core/hooks/use-dictionary"

export function HeaderNavSearch() {
    const [isOpen, setIsOpen] = useState(false)
    const [query, setQuery] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const [recentSearches, setRecentSearches] = useState<string[]>([])
    const inputRef = useRef<HTMLInputElement>(null)
    const router = useRouter()
    const { dictionary } = useDictionary()

    useEffect(() => {
        const saved = localStorage.getItem("recentSearches")
        if (saved) {
            try {
                setRecentSearches(JSON.parse(saved))
            } catch (e) {
                console.error("Failed to parse recent searches:", e)
            }
        }
    }, [])

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus()
        }
    }, [isOpen])

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") setIsOpen(false)
        }
        if (isOpen) {
            document.addEventListener("keydown", handleEscape)
            return () => document.removeEventListener("keydown", handleEscape)
        }
    }, [isOpen])

    const handleSearch = async (searchQuery: string) => {
        if (!searchQuery.trim()) return

        setIsLoading(true)
        try {
            const updated = [searchQuery, ...recentSearches.filter((s) => s !== searchQuery)].slice(0, 5)
            setRecentSearches(updated)
            localStorage.setItem("recentSearches", JSON.stringify(updated))

            router.push(`/shop/category/products/all?search=${encodeURIComponent(searchQuery)}`)
            setIsOpen(false)
            setQuery("")
        } catch (error) {
            console.error("Search error:", error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        handleSearch(query)
    }

    const handleRecentClick = (search: string) => {
        setQuery(search)
        handleSearch(search)
    }

    return (
        <>
            <button
                onClick={() => setIsOpen(true)}
                aria-label="Open search"
                className="flex items-center justify-center size-9 rounded-full border border-primary-300 hover:border-primary-900 hover:bg-primary-100 group-data-[transparent=true]:border-primary-0/40 group-data-[transparent=true]:hover:bg-primary-0/15 group-data-[transparent=true]:hover:border-primary-0 transition-colors"
            >
                <Search className="h-[16px] w-[16px] stroke-[1.4]" />
            </button>

            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center">
                    <div className="absolute inset-0 bg-primary-950/20 backdrop-blur-sm" onClick={() => setIsOpen(false)} />

                    <div className="relative w-full bg-primary-0 animate-in fade-in slide-in-from-top-4 p-6 duration-200 border-b border-primary-200">
                        <form onSubmit={handleSubmit} className="relative max-w-2xl mx-auto">
                            <div className="flex items-center border-b border-primary-300 focus-within:border-primary-900 transition-colors">
                                <div className="flex items-center justify-center text-primary-500">
                                    <Search className="h-[18px] w-[18px] stroke-[1.5]" />
                                </div>

                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={dictionary.ui.product.searchProducts}
                                    className="w-full px-4 py-4 text-primary-900 bg-transparent placeholder:text-primary-500 focus:outline-none"
                                />

                                {isLoading && (
                                    <div className="mr-3">
                                        <div className="h-4 w-4 animate-spin border-2 border-primary-400 border-t-transparent rounded-full" />
                                    </div>
                                )}

                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsOpen(false)
                                        setQuery("")
                                    }}
                                    className="mr-2 p-2 text-primary-500 transition-colors hover:text-primary-900"
                                >
                                    <X className="h-[18px] w-[18px]" />
                                </button>
                            </div>
                        </form>

                        <div className="max-h-96 overflow-y-auto max-w-2xl mx-auto">
                            {recentSearches.length > 0 ? (
                                <div className="pt-4">
                                    <div className="mb-2 px-1 py-2 text-xs tracking-wide text-primary-500">
                                        {dictionary.forms.linkSelector.recentSearches}
                                    </div>
                                    {recentSearches.map((search, index) => (
                                        <div
                                            key={index}
                                            className="flex w-full items-center justify-between gap-3 px-1 py-3 text-sm text-primary-900 border-b border-primary-100 last:border-0"
                                        >
                                            <button
                                                onClick={() => handleRecentClick(search)}
                                                className="flex items-center gap-2 flex-1 text-left"
                                            >
                                                <Clock className="h-4 w-4 text-primary-500" />
                                                <span>{search}</span>
                                            </button>

                                            <button
                                                onClick={() => {
                                                    const updated = recentSearches.filter((s) => s !== search)
                                                    setRecentSearches(updated)
                                                    localStorage.setItem("recentSearches", JSON.stringify(updated))
                                                }}
                                                className="text-primary-400 hover:text-primary-900 transition-colors p-1"
                                                aria-label="حذف جستجو"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}